<template>
  <div class="crypto-dashboard">
    <h1>Crypto Portfolio Summary</h1>

    <div v-if="loading" class="loading">Loading current prices...</div>

    <!-- Year Filter -->
    <div class="year-filter">
      <div class="filter-pills">
        <button
          v-for="year in availableYears"
          :key="year"
          :class="['pill', { active: selectedYear === year }]"
          @click="selectedYear = year"
        >
          {{ year === 'all' ? 'All Years' : year }}
        </button>
      </div>
    </div>

    <!-- Overall Summary -->
    <div class="summary-card">
      <h2>Portfolio Overview</h2>
      <div class="metrics">
        <div class="metric">
          <span class="label">Total Invested (inc fees)</span>
          <span class="value"
            >&euro;{{
              totalInvested.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
            }}</span
          >
        </div>
        <div class="metric">
          <span class="label">Current Value</span>
          <span class="value"
            >&euro;{{
              totalCurrentValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
            }}</span
          >
        </div>
        <div class="metric">
          <span class="label">Total Fees</span>
          <span class="value"
            >&euro;{{ totalFees.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}</span
          >
        </div>
        <div class="metric">
          <span class="label">P&L</span>
          <span class="value" :class="totalPnL >= 0 ? 'profit' : 'loss'">
            &euro;{{ totalPnL.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}
            <br />({{ totalPnLPercent.toFixed(1) }}%)
          </span>
        </div>
      </div>
    </div>

    <!-- By Transaction Type -->
    <div class="summary-card">
      <h2>
        By Transaction Type
        <span v-if="selectedAsset" class="filter-indicator">- {{ selectedAsset }}</span>
        <span v-if="selectedType" class="filter-indicator">- {{ selectedType }}</span>
        <button v-if="selectedType" @click="clearTypeSelection" class="clear-filter">Clear Type</button>
      </h2>
      <div class="type-grid">
        <div
          v-for="type in typesSummary"
          :key="type.name"
          :class="['type-item', { selected: selectedType === type.name }]"
          @click="selectType(type.name)"
        >
          <h3>{{ type.name }}</h3>
          <div class="type-metrics">
            <span
              >&euro;{{
                type.total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
              }}</span
            >
            <small v-if="type.name === 'Staking Income' && type.currentValue > 0" class="current-value">
              Current: €{{
                type.currentValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
              }}
            </small>
            <small>{{ type.count }} transactions</small>
          </div>
        </div>
      </div>
    </div>

    <!-- By Asset -->
    <div class="summary-card">
      <h2>
        Assets Portfolio
        <button v-if="selectedAsset" @click="clearAssetSelection" class="clear-filter">Clear Asset</button>
        <span v-if="selectedType" class="filter-indicator">- {{ selectedType }}</span>
      </h2>
      <p class="note">* Send transactions ignored for quantity (self-transfers), fees included in cost</p>
      <div class="assets-grid">
        <div
          v-for="asset in assetsSummary"
          :key="asset.symbol"
          :class="['asset-item', { selected: selectedAsset === asset.symbol }]"
          @click="selectAsset(asset.symbol)"
        >
          <div class="asset-header">
            <h3>{{ asset.symbol }}</h3>
            <span class="current-price">
              &euro;{{
                asset.currentPrice > 0
                  ? asset.currentPrice.toLocaleString('en-US', { minimumFractionDigits: 4, maximumFractionDigits: 4 })
                  : 'N/A'
              }}
            </span>
          </div>
          <div class="asset-metrics">
            <div class="metric-row">
              <span>Qty inc Sent:</span>
              <span>{{ asset.quantity.toFixed(6) }}</span>
            </div>
            <div class="metric-row">
              <span>Conbase Qty:</span>
              <span>{{ (asset.coinbaseQty || 0).toFixed(6) }}</span>
            </div>
            <div class="metric-row">
              <span>Avg Price:</span>
              <span
                >&euro;{{
                  asset.avgPrice.toLocaleString('en-US', { minimumFractionDigits: 4, maximumFractionDigits: 4 })
                }}</span
              >
            </div>
            <div class="metric-row">
              <span>Subtotal:</span>
              <span>
                &euro;{{
                  asset.subtotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                }}
              </span>
            </div>
            <div class="metric-row">
              <span>Fees:</span>
              <span>
                &euro;{{ asset.fees.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}
              </span>
            </div>
            <div class="metric-row">
              <span>Current Value:</span>
              <span
                >&euro;{{
                  asset.currentValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                }}</span
              >
            </div>
            <div v-if="asset.realizedGains !== 0" class="metric-row">
              <span>Realized Gains:</span>
              <span :class="asset.realizedGains >= 0 ? 'profit' : 'loss'"
                >&euro;{{
                  asset.realizedGains.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                }}</span
              >
            </div>
            <div v-if="asset.earnedQty !== 0" class="metric-row">
              <span>Rewards:</span>
              <span :class="asset.earnedQty >= 0 ? 'profit' : 'loss'">
                {{ asset.earnedQty.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}
                (&euro;{{
                  (asset.earnedQty * asset.currentPrice).toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })
                }})
              </span>
            </div>
            <div class="metric-row">
              <span>P&L:</span>
              <span :class="asset.pnl >= 0 ? 'profit' : 'loss'">
                &euro;{{
                  asset.pnl.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                }}
                ({{ asset.pnlPercent.toFixed(1) }}%)
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Debug Transactions List -->
    <div class="debug-section">
      <div class="summary-card">
        <h2>
          Transactions Debug ({{ filteredItems.length }} total)
          <span v-if="selectedAsset" class="filter-indicator">- {{ selectedAsset }}</span>
          <span v-if="selectedType" class="filter-indicator">- {{ selectedType }}</span>
        </h2>
        <div class="transactions-list">
          <div v-for="item in paginatedItems" :key="item.id" class="transaction-item">
            <div class="transaction-header">
              <span class="type">{{ item.type }}</span>
              <span class="asset">{{ item.asset }}</span>
              <span class="date">{{ item.timestamp }}</span>
            </div>
            <div class="transaction-details">
              <span>Qty: {{ item.quantity }}</span>
              <span>Total: €{{ item.total }}</span>
              <span>Fees: €{{ item.fees }}</span>
            </div>
          </div>
        </div>
        <div class="pagination">
          <button @click="currentPage--" :disabled="currentPage <= 1">Previous</button>
          <span>Page {{ currentPage }} of {{ totalPages }}</span>
          <button @click="currentPage++" :disabled="currentPage >= totalPages">Next</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/assets/api'

export default {
  data() {
    return {
      items: [],
      currentPrices: {},
      loading: false,
      selectedYear: 'all',
      selectedAsset: null,
      selectedType: null,
      currentPage: 1,
      itemsPerPage: 50,
    }
  },
  computed: {
    availableYears() {
      const years = [
        ...new Set(
          this.items
            .map((item) => {
              const date = new Date(item.timestamp.replace(' UTC', 'Z'))
              return isNaN(date.getFullYear()) ? null : date.getFullYear()
            })
            .filter((year) => year !== null)
        ),
      ]
      return ['all', ...years.sort((a, b) => b - a)]
    },
    filteredItems() {
      let items =
        this.selectedYear === 'all'
          ? this.items
          : this.items.filter((item) => {
              const date = new Date(item.timestamp.replace(' UTC', 'Z'))
              return !isNaN(date.getFullYear()) && date.getFullYear() === this.selectedYear
            })

      if (this.selectedAsset) {
        items = items.filter((item) => item.asset === this.selectedAsset)
      }

      if (this.selectedType) {
        items = items.filter((item) => item.type === this.selectedType)
      }

      return items
    },
    totalInvested() {
      return this.filteredItems
        .filter((item) => this.isAssetPurchase(item))
        .reduce((acc, item) => {
          if (item.type === 'Send') {
            return acc + (item.fees || 0) // Only fees for Send transactions
          }
          return acc + (item.total || 0) //Total includes fees (subtotal doesn't)
        }, 0)
    },
    totalFees() {
      return this.filteredItems.reduce((acc, item) => acc + (item.fees || 0), 0)
    },
    totalCurrentValue() {
      return this.assetsSummary.reduce((acc, asset) => acc + asset.totalValue, 0)
    },
    totalPnL() {
      return this.totalCurrentValue - this.totalInvested
    },
    totalPnLPercent() {
      return this.totalInvested > 0 ? (this.totalPnL / this.totalInvested) * 100 : 0
    },
    typesSummary() {
      const types = {}
      this.filteredItems.forEach((item) => {
        if (!types[item.type]) {
          types[item.type] = { total: 0, count: 0, currentValue: 0 }
        }
        types[item.type].total += item.total || 0
        types[item.type].count++

        // Add current value for staking income
        if (item.type === 'Staking Income') {
          const currentPrice = this.currentPrices[item.asset] || 0
          types[item.type].currentValue += (item.quantity || 0) * currentPrice
        }
      })
      return Object.entries(types).map(([name, data]) => ({ name, ...data }))
    },
    assetsSummary() {
      const assets = {}

      // Calculate cumulative holdings up to selected year
      let relevantItems =
        this.selectedYear === 'all'
          ? this.items
          : this.items.filter((item) => {
              const date = new Date(item.timestamp.replace(' UTC', 'Z'))
              return !isNaN(date.getFullYear()) && date.getFullYear() <= this.selectedYear
            })

      // Filter by transaction type if selected
      if (this.selectedType) {
        relevantItems = relevantItems.filter((item) => item.type === this.selectedType)
      }

      relevantItems.forEach((item) => {
        if (!assets[item.asset]) {
          assets[item.asset] = {
            coinbaseQty: 0,
            quantity: 0, //total holdings (disregard sends)
            subtotal: 0,
            fees: 0,
            purchasedQuantity: 0,
            realizedGains: 0,
            earnedQty: 0,
          }
        }
        assets[item.asset].coinbaseQty += item.quantity || 0 // Total quantity including all transactions should match coinbase number
        assets[item.asset].fees += item.fees || 0 // Total fees
        if (this.isAssetPurchase(item)) {
          assets[item.asset].quantity += item.quantity || 0
          assets[item.asset].purchasedQuantity += item.quantity || 0
          assets[item.asset].subtotal += item.subtotal || 0
        } else if (this.isSellTransaction(item)) {
          // Sell transactions: quantities are already negative, so add them directly
          assets[item.asset].quantity += item.quantity || 0
          // For sells, total is negative, so negate it to get positive realized gains
          assets[item.asset].realizedGains += -(item.total || 0) - (item.fees || 0)
        } else if (this.isColdStorageTransaction(item)) {
          // Ignore self-transfers to cold storage for quantity. Fees already included.
        } else if (this.isEarnedTransaction(item)) {
          // Earned crypto (staking, rewards): add quantity, no cost basis
          assets[item.asset].quantity += item.quantity || 0
          assets[item.asset].earnedQty += item.quantity || 0
        }
      })

      return Object.entries(assets)
        .filter(([symbol, data]) => {
          // If filtering by type, show all assets that have that transaction type
          if (this.selectedType) {
            return true // Already filtered above
          }
          // Otherwise, only show assets with holdings > 0.000001
          return data.quantity > 0.000001
        })
        .map(([symbol, data]) => {
          const currentPrice = this.currentPrices[symbol] || 0
          const currentValue = data.quantity * currentPrice
          const totalValue = currentValue + data.realizedGains + data.earnedQty * currentPrice
          const pnl = totalValue - data.subtotal - data.fees
          const pnlPercent = data.subtotal > 0 ? (pnl / data.subtotal) * 100 : 0
          const avgPrice = data.purchasedQuantity > 0 ? data.subtotal / data.purchasedQuantity : 0

          return {
            symbol,
            quantity: data.quantity,
            purchasedQuantity: data.purchasedQuantity,
            coinbaseQty: data.coinbaseQty,
            subtotal: data.subtotal,
            fees: data.fees,
            avgPrice,
            currentPrice,
            currentValue,
            realizedGains: data.realizedGains,
            totalValue,
            pnl,
            pnlPercent,
            earnedQty: data.earnedQty,
          }
        })
        .sort((a, b) => b.subtotal - a.subtotal)
    },
    paginatedItems() {
      const start = (this.currentPage - 1) * this.itemsPerPage
      const end = start + this.itemsPerPage
      return this.filteredItems.slice(start, end)
    },
    totalPages() {
      return Math.ceil(this.filteredItems.length / this.itemsPerPage)
    },
  },
  methods: {
    isAssetPurchase(item) {
      // Actual purchases with fiat money
      return ['Advanced Trade Buy', 'Buy', 'Pro Withdrawal', 'Receive', 'Asset Migration'].includes(item.type)
      // Note on 'Receive':
      // 1. Assuming it is a self-transfer consolidating from other wallets to Coinbase.
      // 2. I think values (price) are the ones at the time of the Receive, which is not the real purchase price. But good enough for now
    },
    isColdStorageTransaction(item) {
      // Self-transfers to cold storage (no cost basis impact)
      return ['Send'].includes(item.type)
    },
    isEarnedTransaction(item) {
      // Free crypto earned (no cost basis)
      return ['Staking Income', 'Reward Income'].includes(item.type)
      //as mentioned earlier, "Receive" is not here because we assume it's self-transfer
    },
    isSellTransaction(item) {
      // Sell transactions that realize gains/losses
      const a = ['Advanced Trade Sell'].includes(item.type)
      const b = item.type == 'Convert' && item.asset !== 'ETH' //TODO: review this condition
      return a || b
    },
    isInternalTransfer(item) {
      // Internal movements between Coinbase products (no cost basis impact)
      return [
        'Deposit',
        'Exchange Deposit',
        'Pro Deposit',
        'Retail Staking Transfer',
        'Retail Unstaking Transfer',
        'Asset Migration',
        'Retail Eth2 Deprecation',
      ].includes(item.type)
    },
    async fetchData() {
      this.loading = true
      try {
        const response = await api.apiFetch('/expenses/merged?type=crypto&refresh=0')
        if (!response.ok) throw new Error('Failed to fetch data')
        const data = await response.json()
        this.items = data.items
        await this.fetchCurrentPrices()
      } catch (error) {
        alert('Error fetching data: ' + error.message)
      }
      this.loading = false
    },
    async fetchCurrentPrices() {
      const assets = [...new Set(this.items.map((item) => item.asset))].filter((asset) => asset !== 'EUR') // Skip fiat currencies

      // Asset symbol mapping for API compatibility
      const symbolMap = {
        POL: 'MATIC', // Polygon rebranded but API might still use MATIC
      }

      try {
        const response = await fetch('https://api.coinbase.com/v2/exchange-rates?currency=EUR')
        if (response.ok) {
          const data = await response.json()
          const rates = data.data.rates

          assets.forEach((asset) => {
            const apiSymbol = symbolMap[asset] || asset
            if (rates[apiSymbol]) {
              // Coinbase API returns 1 EUR = X crypto, we need 1 crypto = X EUR
              const rate = parseFloat(rates[apiSymbol])
              const price = rate > 0 ? 1 / rate : 0
              this.$set(this.currentPrices, asset, price)
            } else {
              this.$set(this.currentPrices, asset, 0)
            }
          })
        } else {
          console.warn('Failed to fetch exchange rates')
        }
      } catch (error) {
        console.warn('Failed to fetch exchange rates:', error)
        this.currentPrices = {}
      }
    },
    selectAsset(symbol) {
      this.selectedAsset = this.selectedAsset === symbol ? null : symbol
      this.currentPage = 1
    },
    clearAssetSelection() {
      this.selectedAsset = null
      this.currentPage = 1
    },
    selectType(type) {
      this.selectedType = this.selectedType === type ? null : type
      this.currentPage = 1
    },
    clearTypeSelection() {
      this.selectedType = null
      this.currentPage = 1
    },
  },
  mounted() {
    this.fetchData()
  },
}
</script>

<style lang="scss" scoped>
.crypto-dashboard {
  padding: 12px;
  max-width: 100%;

  h1 {
    font-size: 1.5rem;
    margin: 0 0 16px 0;
    text-align: center;
  }

  @media (min-width: 768px) {
    max-width: 1200px;
    margin: 0 auto;
    padding: 20px;

    h1 {
      font-size: 2rem;
      text-align: left;
    }
  }
}

.year-filter {
  margin-bottom: 16px;
}

.filter-pills {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  justify-content: center;

  @media (min-width: 768px) {
    justify-content: flex-start;
    gap: 8px;
  }
}

.pill {
  background: #f3f4f6;
  border: 1px solid #d1d5db;
  border-radius: 20px;
  padding: 10px 16px;
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.2s;
  min-height: 44px;
  display: flex;
  align-items: center;
  touch-action: manipulation;

  &:active {
    transform: scale(0.98);
  }

  &.active {
    background: #3b82f6;
    color: white;
    border-color: #3b82f6;
  }

  @media (min-width: 768px) {
    font-size: 0.9rem;
    padding: 8px 16px;
    min-height: auto;

    &:hover {
      background: #e5e7eb;
    }

    &.active:hover {
      background: #2563eb;
    }
  }
}

.debug-section {
  margin-top: 24px;

  @media (min-width: 768px) {
    margin-top: 40px;
  }
}

.transactions-list {
  max-height: 400px;
  overflow-y: auto;

  @media (min-width: 768px) {
    max-height: 600px;
  }
}

.transaction-item {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 8px;
  background: #f9fafb;
}

.transaction-header {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 8px;

  .type {
    font-weight: 600;
    color: #374151;
    font-size: 0.9rem;
  }

  .asset {
    color: #3b82f6;
    font-weight: 500;
    font-size: 0.85rem;
  }

  .date {
    color: #6b7280;
    font-size: 0.8rem;
  }

  @media (min-width: 768px) {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;

    .type {
      font-size: 1rem;
    }

    .asset {
      font-size: 0.9rem;
    }

    .date {
      font-size: 0.9rem;
    }
  }
}

.transaction-details {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 0.8rem;
  color: #6b7280;

  @media (min-width: 768px) {
    flex-direction: row;
    gap: 16px;
    font-size: 0.9rem;
  }
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  margin-top: 16px;
  flex-wrap: wrap;

  button {
    padding: 12px 20px;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    background: white;
    cursor: pointer;
    font-size: 0.9rem;
    min-height: 44px;
    touch-action: manipulation;

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    &:active:not(:disabled) {
      transform: scale(0.98);
      background: #f3f4f6;
    }
  }

  span {
    font-size: 0.85rem;
    white-space: nowrap;
  }

  @media (min-width: 768px) {
    gap: 16px;
    margin-top: 20px;

    button {
      padding: 8px 16px;
      min-height: auto;

      &:hover:not(:disabled) {
        background: #f3f4f6;
      }
    }

    span {
      font-size: 1rem;
    }
  }
}

.loading {
  text-align: center;
  padding: 20px;
  color: #6b7280;
  font-style: italic;
}

.summary-card {
  background: white;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  h2 {
    margin: 0 0 16px 0;
    color: #333;
    font-size: 1.25rem;
    text-align: center;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    flex-wrap: wrap;

    .filter-indicator {
      font-size: 0.9rem;
      color: #3b82f6;
      font-weight: normal;
    }

    .clear-filter {
      background: #ef4444;
      color: white;
      border: none;
      border-radius: 6px;
      padding: 4px 8px;
      font-size: 0.75rem;
      cursor: pointer;
      touch-action: manipulation;

      &:active {
        transform: scale(0.95);
      }
    }
  }

  @media (min-width: 768px) {
    padding: 24px;
    margin-bottom: 24px;

    h2 {
      font-size: 1.5rem;
      text-align: left;
      margin-bottom: 20px;
      justify-content: flex-start;

      .filter-indicator {
        font-size: 1rem;
      }

      .clear-filter {
        font-size: 0.8rem;
        padding: 6px 12px;

        &:hover {
          background: #dc2626;
        }
      }
    }
  }
}

.note {
  font-size: 0.8rem;
  color: #6b7280;
  margin-bottom: 12px;
  font-style: italic;
  text-align: center;

  @media (min-width: 768px) {
    font-size: 0.85rem;
    margin-bottom: 16px;
    text-align: left;
  }
}

.metrics {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;

  @media (min-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 20px;
  }
}

.metric {
  display: flex;
  flex-direction: column;
  text-align: center;
  padding: 12px;
  background: #f8fafc;
  border-radius: 8px;

  .label {
    font-size: 0.8rem;
    color: #666;
    margin-bottom: 4px;
  }

  .value {
    font-size: 1.1rem;
    font-weight: 600;
    word-break: break-all;

    &.profit {
      color: #10b981;
    }
    &.loss {
      color: #ef4444;
    }
  }

  @media (min-width: 768px) {
    text-align: left;
    padding: 0;
    background: transparent;

    .label {
      font-size: 0.9rem;
    }

    .value {
      font-size: 1.5rem;
      word-break: normal;
    }
  }
}

.type-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;

  @media (min-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 16px;
  }
}

.type-item {
  background: #f8fafc;
  padding: 12px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  touch-action: manipulation;
  border: 1px solid transparent;

  &:active {
    transform: scale(0.98);
  }

  &.selected {
    border-color: #3b82f6;
    background: #eff6ff;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
  }

  @media (min-width: 768px) {
    &:hover {
      background: #f1f5f9;
      border-color: #d1d5db;
    }

    &.selected:hover {
      border-color: #2563eb;
    }
  }

  h3 {
    margin: 0 0 8px 0;
    font-size: 0.9rem;
    color: #374151;
  }

  .type-metrics {
    display: flex;
    flex-direction: column;

    span {
      font-weight: 600;
      color: #111827;
      font-size: 0.85rem;
      word-break: break-all;
    }

    small {
      color: #6b7280;
      margin-top: 4px;
      font-size: 0.75rem;

      &.current-value {
        color: #10b981;
        font-weight: 500;
      }
    }
  }

  @media (min-width: 768px) {
    padding: 16px;

    h3 {
      font-size: 1rem;
    }

    .type-metrics {
      span {
        font-size: 1rem;
        word-break: normal;
      }

      small {
        font-size: 0.85rem;
      }
    }
  }
}

.assets-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;

  @media (min-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 20px;
  }
}

.asset-item {
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 16px;
  cursor: pointer;
  transition: all 0.2s;
  touch-action: manipulation;

  &:active {
    transform: scale(0.98);
  }

  &.selected {
    border-color: #3b82f6;
    background: #eff6ff;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
  }

  @media (min-width: 768px) {
    &:hover {
      border-color: #d1d5db;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    }

    &.selected:hover {
      border-color: #2563eb;
    }
  }

  .asset-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;

    h3 {
      margin: 0;
      font-size: 1.1rem;
      color: #111827;
    }

    .current-price {
      font-size: 0.8rem;
      color: #6b7280;
      font-weight: 500;
      word-break: break-all;
      text-align: right;
      max-width: 50%;
    }
  }

  .asset-metrics {
    .metric-row {
      display: flex;
      justify-content: space-between;
      margin-bottom: 6px;
      align-items: flex-start;

      span:first-child {
        color: #6b7280;
        font-size: 0.8rem;
        flex-shrink: 0;
      }

      span:last-child {
        font-weight: 500;
        font-size: 0.8rem;
        text-align: right;
        word-break: break-all;
        max-width: 60%;

        &.profit {
          color: #10b981;
        }
        &.loss {
          color: #ef4444;
        }
      }
    }
  }

  @media (min-width: 768px) {
    padding: 20px;

    .asset-header {
      margin-bottom: 16px;

      h3 {
        font-size: 1.2rem;
      }

      .current-price {
        font-size: 0.9rem;
        word-break: normal;
        max-width: none;
      }
    }

    .asset-metrics {
      .metric-row {
        margin-bottom: 8px;

        span:first-child {
          font-size: 0.9rem;
        }

        span:last-child {
          font-size: 0.9rem;
          word-break: normal;
          max-width: none;
        }
      }
    }
  }
}
</style>
